import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../pages/LoginPage.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

export interface ZweiFaktorEinrichtungProps {
  token: string;
  tokenArt: 'voll' | 'setup';
  onFertig: () => void;
  onAbbrechen?: () => void;
}

type Phase = 'methode' | 'totp' | 'email' | 'backup';

/**
 * Assistent fuer die 2FA-Einrichtung (Spec §6.2). Laeuft in der Konto-Sektion
 * (volles Token) und im erzwungenen Login-Schritt (Setup-Zwischen-Token).
 */
const ZweiFaktorEinrichtung: React.FC<ZweiFaktorEinrichtungProps> = ({ token, tokenArt, onFertig, onAbbrechen }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [phase, setPhase] = useState<Phase>('methode');
  const [emailMoeglich, setEmailMoeglich] = useState(false);
  const [otpauthUri, setOtpauthUri] = useState('');
  const [qr, setQr] = useState('');
  const [emailMaskiert, setEmailMaskiert] = useState('');
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [gesichert, setGesichert] = useState(false);
  const [kopiert, setKopiert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/auth/zwei-faktor/status`, { headers })
      .then(r => setEmailMoeglich(!!r.data.email_versand_moeglich))
      .catch(() => setEmailMoeglich(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const secretAusUri = (uri: string) => {
    const m = /[?&]secret=([A-Z2-7]+)/i.exec(uri);
    return m ? m[1] : '';
  };

  const starteTotp = async () => {
    setFehler(null);
    setLaedt(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/zwei-faktor/totp/starten`, {}, { headers });
      setOtpauthUri(r.data.otpauth_uri);
      setQr(r.data.qr_data_uri);
      setCode('');
      setPhase('totp');
    } catch (e: any) {
      setFehler(e.response?.data?.detail || 'Einrichtung konnte nicht gestartet werden.');
    } finally {
      setLaedt(false);
    }
  };

  const starteEmail = async () => {
    setFehler(null);
    setLaedt(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/zwei-faktor/email/starten`, {}, { headers });
      setEmailMaskiert(r.data.email_maskiert);
      setCode('');
      setPhase('email');
    } catch (e: any) {
      setFehler(e.response?.data?.detail || 'Code konnte nicht gesendet werden.');
    } finally {
      setLaedt(false);
    }
  };

  const bestaetigen = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setFehler(null);
    setLaedt(true);
    try {
      const pfad = phase === 'totp' ? 'totp/bestaetigen' : 'email/bestaetigen';
      const r = await axios.post(`${API_URL}/api/auth/zwei-faktor/${pfad}`, { code }, { headers });
      setBackupCodes(r.data.backup_codes);
      setPhase('backup');
    } catch (e: any) {
      setFehler(e.response?.data?.detail || 'Code ungültig.');
    } finally {
      setLaedt(false);
    }
  };

  const kopieren = () => {
    navigator.clipboard.writeText(backupCodes.join('\n')).then(() => {
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    }).catch(() => setFehler('Kopieren nicht möglich – bitte die Codes markieren und manuell kopieren.'));
  };

  if (phase === 'backup') {
    return (
      <div className="zfa-assistent">
        <h3>Backup-Codes sichern</h3>
        <p className="login-hint">
          Jeder Code funktioniert genau einmal, falls Sie keinen Zugriff auf App bzw. E-Mail haben.
          Diese Liste wird nur jetzt angezeigt.
        </p>
        <pre className="zfa-codes">{backupCodes.join('\n')}</pre>
        {fehler && <div className="login-error">{fehler}</div>}
        <div className="login-links">
          <button type="button" className="login-link" onClick={kopieren}>{kopiert ? 'Kopiert ✓' : 'Codes kopieren'}</button>
        </div>
        <label className="login-check">
          <input type="checkbox" checked={gesichert} onChange={e => setGesichert(e.target.checked)} />
          Ich habe die Codes gesichert
        </label>
        <button className="login-btn" type="button" disabled={!gesichert} onClick={onFertig}>
          Fertig <span className="arr">→</span>
        </button>
      </div>
    );
  }

  if (phase === 'totp' || phase === 'email') {
    return (
      <form className="zfa-assistent" onSubmit={bestaetigen}>
        {phase === 'totp' ? (
          <>
            <h3>Authenticator-App verbinden</h3>
            <p className="login-hint">QR-Code mit Google Authenticator, Microsoft Authenticator o. ä. scannen, dann den angezeigten Code eingeben.</p>
            <img className="zfa-qr" src={qr} alt="QR-Code für die Authenticator-App" />
            <p className="login-hint">Manuell eingeben: <code>{secretAusUri(otpauthUri)}</code></p>
          </>
        ) : (
          <>
            <h3>Code per E-Mail</h3>
            <p className="login-hint">Wir haben einen Code an {emailMaskiert} gesendet.</p>
          </>
        )}
        <div className="login-field">
          <label htmlFor="zfa-code">Code</label>
          <input id="zfa-code" type="text" value={code} onChange={e => setCode(e.target.value)} inputMode="numeric"
                 autoComplete="one-time-code" placeholder="123456" required autoFocus />
        </div>
        {fehler && <div className="login-error">{fehler}</div>}
        <button className="login-btn" type="submit" disabled={laedt}>
          {laedt ? 'Prüfe …' : 'Aktivieren'} <span className="arr">→</span>
        </button>
        <div className="login-links">
          {phase === 'email' && <button type="button" className="login-link" onClick={starteEmail} disabled={laedt}>Code erneut senden</button>}
          <button type="button" className="login-link" onClick={() => { setPhase('methode'); setFehler(null); }}>Andere Methode</button>
        </div>
      </form>
    );
  }

  return (
    <div className="zfa-assistent">
      <h3>Methode wählen</h3>
      <button className="login-btn" type="button" onClick={starteTotp} disabled={laedt}>
        Authenticator-App (empfohlen) <span className="arr">→</span>
      </button>
      <p className="login-hint">Funktioniert offline; Code aus der App auf Ihrem Handy.</p>
      <button className="login-btn" type="button" onClick={starteEmail} disabled={laedt || !emailMoeglich}
              title={emailMoeglich ? '' : 'Mailversand ist nicht eingerichtet'}>
        Code per E-Mail <span className="arr">→</span>
      </button>
      {!emailMoeglich && <p className="login-hint">E-Mail-Codes sind erst möglich, wenn der Mailversand konfiguriert ist.</p>}
      {fehler && <div className="login-error">{fehler}</div>}
      {tokenArt === 'voll' && onAbbrechen && (
        <div className="login-links">
          <button type="button" className="login-link" onClick={onAbbrechen}>Abbrechen</button>
        </div>
      )}
    </div>
  );
};

export default ZweiFaktorEinrichtung;
